import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaPlus, FaEdit, FaTrash, FaExternalLinkAlt } from 'react-icons/fa';
import api from '../services/api';
import { normalizeListForAdmin, toBool } from '../utils/normalizeApi';

function SocialLinksManagement() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    platform: '',
    url: '',
    icon: 'fa-facebook-f',
    displayOrder: 1,
    isActive: true
  });

  const icons = ['fa-facebook-f', 'fa-instagram', 'fa-twitter', 'fa-youtube', 'fa-linkedin', 'fa-github', 'fa-x-twitter'];

  useEffect(() => { fetchLinks(); }, []);

  const fetchLinks = async () => {
    try {
      const response = await api.get('/social/all');
      console.log('🔗 Fetched social links:', response.data);
      setLinks(normalizeListForAdmin(response.data));
    } catch (error) {
      console.error('Error fetching links:', error);
      setLinks([
        { id: 1, platform: 'Facebook', url: 'https://facebook.com', icon: 'fa-facebook-f', isActive: true }
      ]);
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/social/${editingId}`, formData);
        alert('✅ Link updated successfully!');
      } else {
        await api.post('/social', formData);
        alert('✅ Link added successfully!');
      }
      setShowForm(false); setEditingId(null); resetForm(); fetchLinks();
    } catch (error) {
      console.error('CRUD Error:', error);
      alert('❌ Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      platform: item.platform,
      url: item.url,
      icon: item.icon || 'fa-facebook-f',
      displayOrder: item.displayOrder || 1,
      isActive: toBool(item.isActive, true)
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this link?')) return;
    try { await api.delete(`/social/${id}`); alert('✅ Link deleted!'); fetchLinks(); }
    catch (error) { console.error('Delete error:', error); alert('❌ Error deleting link'); }
  };

  const resetForm = () => {
    setFormData({ platform: '', url: '', icon: 'fa-facebook-f', displayOrder: 1, isActive: true });
  };

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-blue-900">Social Links Management</h1>
          <button onClick={() => setShowForm(true)}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2">
            <FaPlus /> Add Link
          </button>
        </motion.div>

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{editingId ? 'Edit' : 'Add'} Social Link</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Platform Name *</label>
                  <input type="text" value={formData.platform} onChange={(e) => setFormData({ ...formData, platform: e.target.value })} required
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., Facebook" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">URL *</label>
                  <input type="url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} required
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                  <select value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                    {icons.map(icon => (<option key={icon} value={icon}>{icon.replace('fa-', '')}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input type="number" value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" min="1" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900" />
                  <label className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold">
                    {editingId ? 'Update' : 'Add'} Link
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold">Cancel</button>
                </div>
              </form>
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {links.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow p-6 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-blue-900 text-white text-xl`}>
                  <i className={`fab ${item.icon}`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-blue-900">{item.platform}</h3>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                    {item.url} <FaExternalLinkAlt size={12} />
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-xs ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {item.isActive ? 'Active' : 'Inactive'}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(item)} className="text-blue-900 hover:text-blue-700"><FaEdit size={18} /></button>
                  <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800"><FaTrash size={18} /></button>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </AdminLayout>
  );
}

export default SocialLinksManagement;
